import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAvatarImage } from '../../pages/Wellness/WellnessDesign';

const AVATAR_COUNT = 6;

const getAvatarIndex = (userId) =>
    userId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % AVATAR_COUNT;

const AUTO_CLOSE_DELAY_MS = 1400;

const FriendInfoModal = memo(({ friend, onClose, onDelete, initialDeleted = false }) => {
    const [avatarSrc, setAvatarSrc] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeleted, setIsDeleted] = useState(initialDeleted);
    const closeTimerRef = useRef(null);

    // 언마운트 시 타이머 정리 (메모리 누수 방지)
    useEffect(() => {
        return () => {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        };
    }, []);

    // initialDeleted = true 인 경우 자동 닫힘 처리
    useEffect(() => {
        if (!initialDeleted) return;
        closeTimerRef.current = setTimeout(onClose, AUTO_CLOSE_DELAY_MS);
    }, [initialDeleted, onClose]);

    useEffect(() => {
        if (!friend?.user_id) return;
        getAvatarImage(getAvatarIndex(friend.user_id))
            .then(src => { if (src) setAvatarSrc(src); })
            .catch(() => {});
    }, [friend?.user_id]);

    const handleDelete = useCallback(async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        const success = await onDelete(friend.user_id);
        if (success) {
            setIsDeleted(true);
            closeTimerRef.current = setTimeout(onClose, AUTO_CLOSE_DELAY_MS);
        } else {
            setIsDeleting(false);
        }
    }, [isDeleting, onDelete, friend?.user_id, onClose]);

    if (!friend) return null;

    const isSleeping = friend.is_active === 1;
    const statusConfig = isSleeping
        ? { label: '자는 중이에요', emoji: '🌙', color: '#5B8DEF', bg: 'rgba(91, 141, 239, 0.1)' }
        : { label: '깨어 있어요', emoji: '☀️', color: '#52B788', bg: 'rgba(82, 183, 136, 0.1)' };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={!isDeleted ? onClose : undefined}
            style={styles.overlay}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 8 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                style={{
                    ...styles.card,
                    padding: isDeleted ? '20px 20px' : '20px 20px 16px',
                }}
            >
                <AnimatePresence mode="wait">
                    {isDeleted ? (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            style={styles.successView}
                        >
                            <p style={styles.successDesc}>친구 삭제가 완료되었습니다.</p>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="info"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={styles.infoView}
                        >
                            {/* 아바타 */}
                            <div style={styles.avatarWrapper}>
                                <div style={styles.avatarCircle}>
                                    {avatarSrc
                                        ? <img src={avatarSrc} alt={friend.friend_name} style={styles.avatarImg} />
                                        : <div style={styles.avatarPlaceholder} />
                                    }
                                </div>
                            </div>

                            {/* 이름 */}
                            <p style={styles.name}>{friend.friend_name}</p>

                            {/* 상태 뱃지 */}
                            <div style={{ ...styles.statusBadge, backgroundColor: statusConfig.bg }}>
                                <span style={{ ...styles.statusText, color: statusConfig.color }}>
                                    {statusConfig.emoji} {statusConfig.label}
                                </span>
                            </div>

                            {/* 버튼 */}
                            <div style={styles.buttonRow}>
                                <motion.button
                                    whileTap={{ opacity: 0.75 }}
                                    onClick={onClose}
                                    style={styles.cancelButton}
                                >
                                    닫기
                                </motion.button>
                                <motion.button
                                    whileTap={{ opacity: 0.75 }}
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                    style={{ ...styles.deleteButton, opacity: isDeleting ? 0.55 : 1 }}
                                >
                                    {isDeleting ? '삭제 중...' : '친구 삭제'}
                                </motion.button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </motion.div>
    );
});

FriendInfoModal.displayName = 'FriendInfoModal';

const FriendInfoBottomSheet = memo(({ friend, onClose, onDelete, initialDeleted = false }) => (
    <AnimatePresence>
        {friend && (
            <FriendInfoModal friend={friend} onClose={onClose} onDelete={onDelete} initialDeleted={initialDeleted} />
        )}
    </AnimatePresence>
));

FriendInfoBottomSheet.displayName = 'FriendInfoBottomSheet';

const styles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        zIndex: 99999,
        padding: '0 32px',
    },
    card: {
        width: '100%',
        maxWidth: '300px',
        backgroundColor: '#FFFFFF',
        borderRadius: '20px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.06)',
    },
    avatarWrapper: {
        position: 'relative',
        marginBottom: '8px',
    },
    avatarCircle: {
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        backgroundColor: '#F5F5F5',
        overflow: 'hidden',
    },
    avatarImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    avatarPlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: '#EBEBEB',
    },
    name: {
        fontSize: '16px',
        fontWeight: '500',
        color: '#111111',
        fontFamily: 'Pretendard',
        letterSpacing: '-0.3px',
        margin: '0 0 6px',
        textAlign: 'center',
    },
    statusBadge: {
        borderRadius: '100px',
        padding: '4px 12px',
        marginBottom: '0px',
    },
    statusText: {
        fontSize: '13px',
        fontWeight: '400',
        fontFamily: 'Pretendard',
        letterSpacing: '-0.2px',
    },
    infoView: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
    },
    successView: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0',
    },
    successDesc: {
        fontSize: '15px',
        fontWeight: '400',
        color: '#333333',
        fontFamily: 'Pretendard',
        letterSpacing: '-0.3px',
        margin: 0,
    },
    buttonRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        width: '100%',
        marginTop: '16px',
    },
    cancelButton: {
        flex: 1,
        padding: '12px 0',
        borderRadius: '10px',
        backgroundColor: '#F2F2F2',
        border: 'none',
        fontSize: '14px',
        fontFamily: 'Pretendard',
        fontWeight: '500',
        color: '#333333',
        letterSpacing: '-0.2px',
        cursor: 'pointer',
        textAlign: 'center',
    },
    deleteButton: {
        flex: 1,
        padding: '10px 0',
        borderRadius: '10px',
        backgroundColor: '#FF7051',
        border: 'none',
        fontSize: '14px',
        fontFamily: 'Pretendard',
        fontWeight: '500',
        color: '#FFFFFF',
        letterSpacing: '-0.2px',
        cursor: 'pointer',
        textAlign: 'center',
    },
};

export default FriendInfoBottomSheet;
